/* External */
import {
  memo, useCallback, useEffect, useState, useRef,
} from 'react';
import { useHistory } from 'react-router-dom';
import { withFocusable } from '@noriginmedia/react-spatial-navigation';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

/* Theme Assets */
import Logo from '~/assets/theme/svg/brand-logo.svg';

/* Other */
import { getJsonImage } from 'src/theme/images';

/* Components */
import FocusableCardNewProfile from '../../../../components/FocusableCardNewProfile';
import FocusableButtonprofile from '../../../../components/FocusableButtonProfile';
import FocusableCardProfileEdit from '../../../../components/FocusableCardProfileEdit';

/* Utils */
import { colors, TYPE_ACTIONS_PROFILE } from '../../../../utils/constants';
import { Routes, getLink } from '../../../../utils/router';
import { useCountry } from '../../../../providers/ConfigProvider';
import { useGtmLocalContext } from '../../../../providers/GtmLocalProvider';
import { useOnBackPress } from '../../../../utils/keyDownEvents';

/* GTM */
import { GTM_CATEGORIES, GTM_EVENT_NAMES, GTM_SUBCATEGORIES } from '../../../../utils/googleTags';

/* Style */
import './ManagementProfile.scss';

function ManagementProfile({ realFocusKey, setFocus }) {
  const userProfiles = useSelector((state) => state.profile.profiles) ?? JSON.parse(localStorage.getItem('profiles'));
  const [profiles] = useState(userProfiles);
  const history = useHistory();
  const profileManagementFocusKey = `${realFocusKey}-first-focus-management-profile`;
  const country = useCountry();
  const { commonTaggingEvent } = useGtmLocalContext();
  const tagsMountRef = useRef(true);

  const profileCommonTagEvent = useCallback(({ eventName }) => {
    if (eventName) {
      commonTaggingEvent({
        category: GTM_CATEGORIES.PROFILES_DATA,
        subCategory: GTM_SUBCATEGORIES.PROFILE.PROFILES_PAGE_EDIT_CREATE,
        eventName,
      });
    }
  }, [commonTaggingEvent]);

  useEffect(() => {
    setFocus(profileManagementFocusKey);
  }, [profileManagementFocusKey, setFocus]);

  useEffect(() => {
    if (tagsMountRef.current && country) {
      profileCommonTagEvent({ eventName: GTM_EVENT_NAMES.PAGE_VIEW });
      tagsMountRef.current = false;
    }
  }, [profileCommonTagEvent, country]);

  const handleClick = useCallback(() => {
    profileCommonTagEvent({ eventName: GTM_EVENT_NAMES.PROFILES_EDIT_CREATE.MANAGE_PROFILES_BACK });
    history.push(getLink(Routes.Private.Profiles));
  }, [history, profileCommonTagEvent]);

  const editUser = useCallback(({ id, indexColor }) => {
    profileCommonTagEvent({ eventName: GTM_EVENT_NAMES.PROFILES_EDIT_CREATE.SELECT_EDIT_PROFILE });
    history.push(getLink(Routes.Private.Profiles.Edit, { id }), { indexColor });
  }, [history, profileCommonTagEvent]);

  const newProfile = useCallback(() => {
    profileCommonTagEvent({ eventName: GTM_EVENT_NAMES.PROFILES_EDIT_CREATE.CREATE_PROFILE });
    history.push(
      getLink(Routes.Private.Profiles.Create.Name),
      { typeCreateName: TYPE_ACTIONS_PROFILE.EDIT },
    );
  }, [history, profileCommonTagEvent]);

  useOnBackPress({
    listener: () => {
      profileCommonTagEvent({
        eventName: GTM_EVENT_NAMES.PROFILES_EDIT_CREATE.MANAGE_PROFILES_BACK,
      });
      history.goBack();
    },
  });

  return (
    <div className="dtv-html5-c-profile-management">
      <img src={getJsonImage('brand-logo') || Logo} alt="dtv-logo" className="dtv-html5-c-profile-management--logo" />
      <div className="dtv-html5-c-profile-management--title">
        <FormattedMessage id="profile.manage" />
      </div>
      <div className="dtv-html5-c-profile-management--cards-profile">
        {profiles?.map((profile, index) => (
          <div className="dtv-html5-c-profile-management--card-edit" key={profile?.id}>
            <FocusableCardProfileEdit
              id={profile?.id}
              indexColor={index + 1}
              name={profile?.name}
              color={colors[index + 1]}
              focusKey={index === 0 ? profileManagementFocusKey : ''}
              onEnterPress={editUser}
            />
          </div>
        ))}
        {profiles?.length < 5 ? (
          <FocusableCardNewProfile
            text={<FormattedMessage id="profile.create" />}
            onEnterPress={newProfile}
          />
        )
          : ''}
      </div>
      <FocusableButtonprofile
        onEnterPress={handleClick}
        className="dtv-btn-tv dtv-html5-c-profile-management--button"
        type="submit"
        secondGray
        bordered
      >
        <FormattedMessage id="common.back" />
      </FocusableButtonprofile>
    </div>
  );
}

ManagementProfile.propTypes = {
  realFocusKey: PropTypes.string.isRequired,
  setFocus: PropTypes.func.isRequired,
};

export default memo(withFocusable()(memo(ManagementProfile)));
